import React, { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, LogOut, FileText, Download, CalendarClock, Sparkles, Wallet, Banknote, ChevronDown, ArrowDownToLine, ArrowRight, Repeat } from "lucide-react";
import Site from "@/pages/Site";

const fmt2 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const field = "w-full rounded-none border border-black bg-white h-12 px-4 text-black focus:outline-none focus:ring-0";

export default function Portal() {
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const asToken = params.get("as");
  const verifyToken = params.get("token");
  const isVerify = window.location.pathname.startsWith("/portal/verify");
  const isReset = window.location.pathname.startsWith("/portal/reset");

  const [impersonating] = useState(!!asToken);
  const [token, setToken] = useState(() => asToken || localStorage.getItem("portal_token") || "");
  const [me, setMe] = useState(null);
  const [roomView, setRoomView] = useState("dashboard");
  const [roomDeal, setRoomDeal] = useState(null);

  const logout = () => {
    if (impersonating) { window.location.href = "/"; return; }
    localStorage.removeItem("portal_token"); setToken(""); setMe(null);
  };

  const load = () => {
    if (!token) return;
    api.get("/portal/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setMe(data))
      .catch(() => { if (!impersonating) logout(); });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (isVerify) return <VerifyView verifyToken={verifyToken} />;
  if (isReset) return <ResetView resetToken={verifyToken} />;
  if (token && me) {
    if (roomView === "deck" && roomDeal)
      return <Site dealId={roomDeal} onBack={() => setRoomView("dashboard")} onLock={() => setRoomView("dashboard")} onAdmin={() => {}} />;
    return <Dashboard me={me} token={token} reload={load} onLogout={logout} impersonating={impersonating} onOpenDeal={(id) => { setRoomDeal(id); setRoomView("deck"); }} />;
  }
  return <AuthView onToken={(t) => { localStorage.setItem("portal_token", t); setToken(t); }} />;
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-neutral-100 text-black grain">
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <div className="mono text-sm font-semibold tracking-[0.2em]">CANIHAVEMONEY&nbsp;LLC</div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-3 inline-flex items-center gap-2"><Lock size={11} /> Investor Portal</div>
      </div>
      <div className="border border-black bg-white p-6">{children}</div>
    </div>
  </div>
);

function AuthView({ onToken }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [setupDone, setSetupDone] = useState(() => !!localStorage.getItem("portal_setup_done"));
  const base = window.location.origin;

  const login = async () => {
    if (!email || !pin) return toast.error("Enter your email and code");
    setBusy(true);
    try {
      const { data } = await api.post("/portal/login", { email, pin });
      onToken(data.token);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const setup = async () => {
    if (!email) return toast.error("Enter your email");
    if (!/^\d{4,6}$/.test(pin)) return toast.error("Choose a 4–6 digit code");
    if (pin !== pin2) return toast.error("Codes don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/portal/setup", { email, pin });
      localStorage.setItem("portal_setup_done", "1");
      toast.success(data.message || "You're all set — welcome in.");
      onToken(data.token);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const verifyCode = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code from your email");
    setBusy(true);
    try {
      const { data } = await api.post("/portal/verify-code", { email, code });
      toast.success("Verified — you're in.");
      onToken(data.token);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const resendCode = async () => {
    if (!email) return toast.error("Enter your email");
    setBusy(true);
    try { const { data } = await api.post("/portal/resend-code", { email }); toast.success(data.message || "A new code is on its way."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const forgot = async () => {
    if (!email) return toast.error("Enter your email");
    setBusy(true);
    try {
      const { data } = await api.post("/portal/forgot", { email, base_url: base });
      toast.success(data.message || "Reset link sent.");
      setMode("login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="mb-6 text-center">
        <div data-testid="portal-mode-title" className="text-lg font-black tracking-tighter">{mode === "login" ? "Sign In" : mode === "setup" ? "Set Up Access" : mode === "verify" ? "Enter Your Code" : "Reset Your Code"}</div>
      </div>

      <label className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email</label>
      <input data-testid="portal-email" value={email} onChange={(e) => setEmail(e.target.value)} readOnly={mode === "verify"} className={`${field} mt-1.5 mb-4 ${mode === "verify" ? "bg-neutral-100" : ""}`} placeholder="you@email.com" type="email" />

      {(mode === "login" || mode === "setup") && (
        <>
          <label className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{mode === "setup" ? "Choose a 4–6 digit code" : "Your code"}</label>
          <input data-testid="portal-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} className={`${field} mt-1.5 tracking-[0.5em]`} placeholder="••••" inputMode="numeric" type="password" />
        </>
      )}
      {mode === "setup" && (
        <>
          <label className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-4 block">Confirm code</label>
          <input data-testid="portal-pin2" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} className={`${field} mt-1.5 tracking-[0.5em]`} placeholder="••••" inputMode="numeric" type="password" />
        </>
      )}
      {mode === "verify" && (
        <>
          <label className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">6-digit code from your email</label>
          <input data-testid="portal-code" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} className={`${field} mt-1.5 tracking-[0.5em]`} placeholder="••••••" inputMode="numeric" />
        </>
      )}

      <button data-testid="portal-submit" disabled={busy}
        onClick={mode === "login" ? login : mode === "setup" ? setup : mode === "verify" ? verifyCode : forgot}
        className="w-full mt-6 h-12 bg-black text-white mono text-xs uppercase tracking-[0.2em] hover:bg-neutral-800 transition-colors disabled:opacity-60">
        {busy ? "Please wait…" : mode === "login" ? "Sign In" : mode === "setup" ? "Create & Enter" : mode === "verify" ? "Activate & Sign In" : "Send Reset Link"}
      </button>

      <div className="mt-5 text-center space-y-2">
        {mode === "login" ? (
          <>
            <button data-testid="portal-forgot" onClick={() => setMode("forgot")} className="block w-full mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-black">Forgot code?</button>
            {!setupDone && <button data-testid="tab-setup" onClick={() => setMode("setup")} className="block w-full mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-black">First time? Set up access</button>}
          </>
        ) : mode === "verify" ? (
          <>
            <button data-testid="portal-resend" onClick={resendCode} className="block w-full mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-black">Didn't get it? Resend code</button>
            <button data-testid="tab-login" onClick={() => setMode("login")} className="block w-full mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-black">Back to sign in</button>
          </>
        ) : (
          <button data-testid="tab-login" onClick={() => setMode("login")} className="mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-black">Back to sign in</button>
        )}
      </div>
      {mode === "setup" && <p className="mono text-[10px] text-neutral-500 mt-4 leading-relaxed">Only investors already on our list can register. Pick a 4–6 digit code and you're in right away — next time, just sign in with your email + code.</p>}
      {mode === "verify" && <p className="mono text-[10px] text-neutral-500 mt-4 leading-relaxed">We emailed a 6-digit code to {email || "your inbox"}. Enter it above to activate your access. The code expires in 30 minutes.</p>}
    </Shell>
  );
}

function VerifyView({ verifyToken }) {
  const [state, setState] = useState("working");
  useEffect(() => {
    if (!verifyToken) { setState("fail"); return; }
    api.post("/portal/verify", { token: verifyToken }).then(() => setState("ok")).catch(() => setState("fail"));
  }, [verifyToken]);
  return (
    <Shell>
      <div className="text-center">
        {state === "working" && <p className="mono text-sm">Verifying…</p>}
        {state === "ok" && <>
          <div data-testid="verify-ok" className="text-2xl font-black tracking-tighter mb-3">You're verified ✓</div>
          <p className="text-sm text-neutral-600 mb-6">Your investor portal access is active. Sign in with your email and code.</p>
          <a href="/portal" data-testid="verify-signin" className="inline-block h-12 leading-[3rem] px-8 bg-black text-white mono text-xs uppercase tracking-[0.2em]">Go to Sign In</a>
        </>}
        {state === "fail" && <>
          <div className="text-xl font-black tracking-tighter mb-3">Link invalid or expired</div>
          <p className="text-sm text-neutral-600 mb-6">Please set up access again to get a fresh verification email.</p>
          <a href="/portal" className="inline-block h-12 leading-[3rem] px-8 bg-black text-white mono text-xs uppercase tracking-[0.2em]">Back to Portal</a>
        </>}
      </div>
    </Shell>
  );
}

function ResetView({ resetToken }) {
  const [pin, setPin] = useState(""); const [pin2, setPin2] = useState(""); const [busy, setBusy] = useState(false); const [done, setDone] = useState(false);
  const submit = async () => {
    if (!/^\d{4,6}$/.test(pin)) return toast.error("Choose a 4–6 digit code");
    if (pin !== pin2) return toast.error("Codes don't match");
    setBusy(true);
    try { await api.post("/portal/reset", { token: resetToken, pin }); setDone(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Shell>
      {done ? (
        <div className="text-center">
          <div className="text-2xl font-black tracking-tighter mb-3">Code updated ✓</div>
          <a href="/portal" className="inline-block h-12 leading-[3rem] px-8 bg-black text-white mono text-xs uppercase tracking-[0.2em]">Sign In</a>
        </div>
      ) : (<>
        <div className="text-lg font-black tracking-tighter mb-4">Choose a new code</div>
        <input data-testid="reset-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} className={`${field} tracking-[0.5em]`} placeholder="••••" inputMode="numeric" type="password" />
        <input data-testid="reset-pin2" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} className={`${field} tracking-[0.5em] mt-3`} placeholder="Confirm" inputMode="numeric" type="password" />
        <button data-testid="reset-submit" disabled={busy} onClick={submit} className="w-full mt-6 h-12 bg-black text-white mono text-xs uppercase tracking-[0.2em] disabled:opacity-60">{busy ? "Saving…" : "Update Code"}</button>
      </>)}
    </Shell>
  );
}

function Dashboard({ me, token, reload, onLogout, impersonating, onOpenDeal }) {
  const b = me.balances || [];
  const w = me.wallet || {};
  const paidPct = w.projected_total ? Math.min(100, Math.max(0, ((w.total_received || 0) / w.projected_total) * 100)) : 0;
  const litSegs = paidPct > 0 ? Math.max(1, Math.round(paidPct / 5)) : 0;
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [openBreak, setOpenBreak] = useState(null);
  const [bankOpen, setBankOpen] = useState(false);
  const [showPayout, setShowPayout] = useState(false);
  const hasBank = !!(me.bank && (me.bank.account_number || me.bank.bank_name));

  return (
    <div className="min-h-screen bg-neutral-100 text-black">
      {impersonating && (
        <div data-testid="impersonation-banner" className="bg-yellow-300 text-black text-center mono text-[10px] uppercase tracking-[0.2em] py-2 px-4">
          Admin preview — viewing as {me.investor.name || me.investor.email}
          <button data-testid="exit-preview" onClick={onLogout} className="underline underline-offset-2 ml-3">Exit preview</button>
        </div>
      )}
      {showPayout && <PayoutModal available={w.total_available || 0} hasBank={hasBank} authHeaders={authHeaders} onClose={() => setShowPayout(false)} onOpenBank={() => { setShowPayout(false); setBankOpen(true); }} onDone={() => { setShowPayout(false); reload(); }} />}

      <header className="bg-black text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="mono text-xs tracking-[0.2em]">CANIHAVEMONEY&nbsp;LLC</div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-1">Investor Portal</div>
          </div>
          <div className="flex items-center gap-5">
            <button data-testid="portal-logout" onClick={onLogout} className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.2em] hover:text-neutral-300"><LogOut size={13} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 data-testid="portal-welcome" className="text-3xl sm:text-4xl font-black tracking-tighter">Welcome, {me.investor.name || "Investor"}</h1>
        <p className="mono text-xs text-neutral-500 mt-1">{me.investor.email}</p>

        {/* HERO — what's yours right now */}
        <div data-testid="portal-wallet" className="mt-8 border-2 border-black bg-black text-white p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-400 inline-flex items-center gap-2"><Wallet size={13} /> Your Money Right Now</div>
              <div data-testid="wallet-balance" className="text-5xl sm:text-6xl font-black tracking-tighter mt-3 tabular-nums">{fmt2(w.total_received || 0)}</div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {w.return_rate != null && <span data-testid="wallet-rate" className="mono text-[9px] uppercase tracking-[0.2em] bg-white text-black px-2.5 py-1 font-black">Earning {w.return_rate}%</span>}
                <span data-testid="wallet-deal-count" className="mono text-[9px] uppercase tracking-[0.2em] border border-white/40 px-2.5 py-1">{w.active_deals || 0} active {(w.active_deals || 0) === 1 ? "position" : "positions"}</span>
              </div>
              <div className="mono text-[11px] text-neutral-400 mt-2">Everything you've been paid so far{(w.total_reinvested || 0) > 0 ? ", after money moved into new deals" : ""}</div>
              <div className="mt-4 inline-flex items-center gap-3 border border-white/30 px-4 py-2">
                <span className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">Available to withdraw</span>
                <span data-testid="wallet-available" className="font-black tabular-nums">{fmt2(w.total_available || 0)}</span>
              </div>
            </div>
            <div className="flex gap-3 shrink-0">
              <button data-testid="wallet-bank-btn" onClick={() => setBankOpen((o) => !o)} className="inline-flex items-center gap-2 border border-white/40 px-4 h-11 mono text-[10px] uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors">
                <Banknote size={13} /> {hasBank ? "Edit Wire" : "Add Wire"}
              </button>
              <button data-testid="wallet-payout-btn" onClick={() => setShowPayout(true)} className="inline-flex items-center gap-2 bg-white text-black px-4 h-11 mono text-[10px] uppercase tracking-[0.2em] hover:bg-neutral-200 transition-colors">
                <ArrowDownToLine size={13} /> Request Payout
              </button>
            </div>
          </div>
          <p data-testid="wallet-withdraw-note" className="mono text-[10px] text-neutral-500 mt-5 leading-relaxed">Withdrawals unlock once your principal is paid back in full — profit unlocks at the end of the term.</p>
          {(w.total_reinvested || 0) > 0 && (
            <div data-testid="wallet-reinvested" className="mt-3 mono text-[10px] text-amber-300/90 flex items-center gap-2">
              <Repeat size={12} /><span><span className="font-semibold">−{fmt2(w.total_reinvested)}</span> was rolled into new deals, so it's not counted twice here.</span>
            </div>
          )}
        </div>

        {/* PROGRESS — how far along */}
        <div data-testid="portal-progress" className="mt-6 border-2 border-black bg-white p-5 sm:p-6">
          <div className="flex items-end justify-between mb-3">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">How far along you are</div>
            <div data-testid="portal-paid-pct" className="text-3xl font-black tracking-tighter tabular-nums leading-none">{paidPct.toFixed(1)}%</div>
          </div>
          <div data-testid="portal-power-bar" className="flex gap-[3px] h-5">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className={`flex-1 transition-colors ${i < litSegs ? "bg-black" : "bg-neutral-200"}`} />
            ))}
          </div>
          <div className="mono text-[10px] text-neutral-500 mt-2">{fmt2(w.total_received || 0)} received of {fmt2(w.projected_total || 0)} you'll get by the end</div>
        </div>

        {/* LEDGER — the story so far */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 border-2 border-black bg-white" data-testid="portal-ledger">
          <div className="p-5">
            <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Capital You Put In</div>
            <div data-testid="ledger-invested" className="text-2xl font-black tracking-tighter mt-1 tabular-nums">{fmt2(w.original_capital || 0)}</div>
            <div className="mono text-[9px] text-neutral-500 mt-1">Your own money invested</div>
          </div>
          <div className="p-5 border-t-2 sm:border-t-0 sm:border-l-2 border-black">
            <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Paid Out To You</div>
            <div data-testid="wallet-paid" className="text-2xl font-black tracking-tighter mt-1 tabular-nums">{fmt2(w.total_paid || 0)}</div>
            <div className="mono text-[9px] text-neutral-500 mt-1">Already withdrawn to your bank</div>
          </div>
          <div className="p-5 border-t-2 sm:border-t-0 sm:border-l-2 border-black">
            <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Still Owed To You</div>
            <div data-testid="ledger-remaining" className="text-2xl font-black tracking-tighter mt-1 tabular-nums">{fmt2(w.total_remaining || 0)}</div>
            <div className="mono text-[9px] text-neutral-500 mt-1">Left to be paid over the term</div>
          </div>
        </div>

        {/* PROJECTION — what you'll walk away with */}
        <div data-testid="portal-projected" className="mt-6 border-t-2 border-black pt-6">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-4">What you'll walk away with — by the end of every deal</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 border-2 border-black">
            <div className="p-5">
              <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Your Principal Back</div>
              <div data-testid="portal-principal" className="text-2xl font-black tracking-tighter mt-1 tabular-nums">{fmt2(w.original_capital || 0)}</div>
              <div className="mono text-[9px] text-neutral-500 mt-1">The money you put in, returned</div>
            </div>
            <div className="p-5 border-t-2 sm:border-t-0 sm:border-l-2 border-black">
              <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Profit Earned</div>
              <div data-testid="portal-profit" className="text-2xl font-black tracking-tighter mt-1 tabular-nums">{fmt2(w.projected_profit || 0)}</div>
              <div className="mono text-[9px] text-neutral-500 mt-1">Your return on top</div>
            </div>
            <div className="p-5 bg-black text-white border-t-2 sm:border-t-0 sm:border-l-2 border-black">
              <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">Total You Receive</div>
              <div data-testid="portal-payout" className="text-2xl font-black tracking-tighter mt-1 tabular-nums">{fmt2(w.projected_total || 0)}</div>
              <div className="mono text-[9px] text-neutral-500 mt-1">Principal + profit</div>
            </div>
          </div>
        </div>

        {(me.shared_deals || []).length > 0 && (
          <div className="mt-6 border-2 border-black bg-white" data-testid="shared-deals">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-black bg-black text-white">
              <Sparkles size={14} /><span className="mono text-[10px] uppercase tracking-[0.25em]">Your Deals</span>
            </div>
            <div className="p-4 grid sm:grid-cols-2 gap-3">
              {me.shared_deals.map((d) => (
                <button key={d.deal_id} data-testid={`shared-deal-${d.deal_id}`} onClick={() => onOpenDeal(d.deal_id)} className="group text-left border border-black p-4 hover:bg-black hover:text-white transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold">{d.deal_name || "Untitled Deal"}</div>
                    <ArrowRight size={16} className="opacity-40 group-hover:opacity-100" />
                  </div>
                  <div className="mono text-[10px] text-neutral-500 group-hover:text-neutral-300 mt-1">{d.return_pct || 0}% return · {d.term_days || 0} days · {d.status === "closed" ? "Closed" : "Open"}{d.invested ? " · You're invested" : ""}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* PAYOUT HISTORY */}
        <div className="mt-6 border border-black bg-white" data-testid="payout-history">
          <div className="flex items-center justify-between px-4 py-3 border-b border-black">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Payout History</span>
            <span className="mono text-[10px] text-neutral-500">Total paid: {fmt2(w.total_paid || 0)}</span>
          </div>
          {(me.payouts || []).length === 0 ? (
            <div data-testid="payout-history-empty" className="px-4 py-6 mono text-[11px] text-neutral-500">No payouts yet. Request one from your wallet above and it will appear here.</div>
          ) : (
            me.payouts.map((p, i) => (
              <div key={p._id} data-testid={`payout-row-${i}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-200 last:border-b-0">
                <div>
                  <div className="font-semibold">{fmt2(p.amount)}</div>
                  <div className="mono text-[10px] text-neutral-500">
                    Requested {(p.created_at || "").slice(0, 10)}
                    {p.status === "paid" && p.paid_at ? ` · Paid ${p.paid_at.slice(0, 10)}` : ""}
                    {p.note ? ` · ${p.note}` : ""}
                  </div>
                </div>
                <PayoutBadge status={p.status} />
              </div>
            ))
          )}
        </div>

        {/* WIRE / BANK DETAILS (dropdown) */}
        <div className="mt-6 border border-black bg-white" data-testid="wire-dropdown">
          <button data-testid="wire-toggle" onClick={() => setBankOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 inline-flex items-center gap-2"><Banknote size={13} /> Wire / Bank Details{hasBank ? "" : " · not set"}</span>
            <ChevronDown size={15} className={`transition-transform ${bankOpen ? "rotate-180" : ""}`} />
          </button>
          {bankOpen && <BankForm bank={me.bank} authHeaders={authHeaders} onSaved={reload} />}
        </div>

        <h2 className="text-xl font-black tracking-tighter mt-12 mb-4 pb-2 border-b border-black">Your Positions</h2>
        {b.length === 0 && <p className="mono text-sm text-neutral-500">No positions on file yet.</p>}
        <div className="grid md:grid-cols-2 gap-4">
          {b.map((x, idx) => (
            <div key={idx} data-testid={`portal-balance-${idx}`} className="border border-black bg-white p-5">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold">{x.deal_name}</div>
                  {x.relation && <div data-testid={`position-relation-${idx}`} className="mono text-[9px] uppercase tracking-[0.15em] text-neutral-500 mt-0.5">{x.relation === "refi" ? "Refi" : "Add-on"} of {x.parent_name}</div>}
                </div>
                {x.schedule && <span className="mono text-[10px] text-neutral-500">{x.share_pct}% · {x.payments_made}/{x.payments_total} paid</span>}
              </div>
              {x.reinvested && (
                <div data-testid={`position-reinvested-${idx}`} className="mt-2 flex items-start gap-2 border border-amber-300 bg-amber-50 px-2.5 py-1.5">
                  <Repeat size={12} className="mt-0.5 shrink-0 text-amber-700" />
                  <div className="mono text-[10px] leading-relaxed text-amber-800">
                    <span className="uppercase tracking-[0.15em] font-semibold">Reinvested</span> · funded with {fmt2(x.reinvested_amount || x.started_with)} rolled from your earlier payouts. This amount is netted from your Active Balance so it isn't counted twice.
                  </div>
                </div>
              )}
              {x.schedule ? (
                <>
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <Cell label="Started With" v={fmt2(x.started_with)} />
                    <Cell label="Received" v={fmt2(x.received)} green big />
                    <Cell label="Remaining" v={fmt2(x.remaining)} />
                    <Cell label="% Paid In" v={`${x.total_return ? Math.round((x.received / x.total_return) * 100) : 0}%`} />
                  </div>
                  <div className="mono text-[10px] text-neutral-500 mt-3 inline-flex items-center gap-1"><CalendarClock size={11} /> {fmt2(x.per_payment)}/day · {x.schedule.next_date ? `next ${x.schedule.next_date}` : "complete"}</div>
                  <button data-testid={`breakdown-toggle-${idx}`} onClick={() => setOpenBreak(openBreak === idx ? null : idx)} className="mt-3 w-full inline-flex items-center justify-between mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 hover:text-black border-t border-neutral-200 pt-3">
                    How this is calculated <ChevronDown size={13} className={`transition-transform ${openBreak === idx ? "rotate-180" : ""}`} />
                  </button>
                  {openBreak === idx && <Breakdown x={x} />}
                </>
              ) : (
                <div className="mono text-[11px] text-neutral-500 mt-3">Committed {fmt2(x.amount)} · schedule not set yet</div>
              )}
            </div>
          ))}
        </div>

        <h2 className="text-xl font-black tracking-tighter mt-14 mb-4 pb-2 border-b border-black">Your Documents</h2>
        {(!me.documents || me.documents.length === 0) && <p className="mono text-sm text-neutral-500">No documents yet.</p>}
        <div className="space-y-2">
          {(me.documents || []).map((d, idx) => (
            <div key={idx} data-testid={`portal-doc-${idx}`} className="border border-black bg-white px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={16} className="shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium truncate">Participation Agreement — {d.deal_name}</div>
                  <div className="mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">{d.status === "signed" ? `Signed ${(d.signed_at || "").slice(0, 10)}` : "Awaiting your signature"}</div>
                </div>
              </div>
              {d.downloadable
                ? <a data-testid={`portal-doc-dl-${idx}`} href={`${API}/sign/${d.token}/document.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.15em] border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors shrink-0"><Download size={12} /> Download</a>
                : <a data-testid={`portal-doc-sign-${idx}`} href={`/sign/${d.token}`} className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.15em] border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors shrink-0"><Sparkles size={12} /> Sign</a>}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

const PayoutBadge = ({ status }) => {
  const map = { requested: "bg-yellow-100 text-yellow-800 border-yellow-300", paid: "bg-green-100 text-green-800 border-green-300", declined: "bg-neutral-100 text-neutral-600 border-neutral-300" };
  return <span data-testid="payout-status" className={`mono text-[9px] uppercase tracking-[0.2em] border px-2.5 py-1 ${map[status] || map.requested}`}>{status}</span>;
};

function Breakdown({ x }) {
  const rows = [
    ["Your principal (started with)", fmt2(x.started_with)],
    ...(x.reinvested ? [["Reinvested from earlier payouts", fmt2(x.reinvested_amount || x.started_with)]] : []),
    ["Your share of the deal", `${x.share_pct}%`],
    ["Total payback (your fixed return)", fmt2(x.total_return)],
    ["Total profit over term", fmt2((x.total_return || 0) - (x.started_with || 0))],
    ...(x.fee_credit ? [["Fee credited to you up front", fmt2(x.fee_credit)]] : []),
    ["Daily payment (your share)", fmt2(x.per_payment)],
    ["Business days elapsed / total", `${x.payments_made} / ${x.payments_total}`],
    ["Received so far", fmt2(x.received)],
    ["Remaining owed to you", fmt2(x.remaining)],
    ["Principal repaid by", x.principal_date || "—"],
    ["Principal recovered", x.principal_recovered ? "Yes" : "Not yet"],
    ["Available to withdraw", fmt2(x.withdrawable != null ? x.withdrawable : 0)],
  ];
  return (
    <div data-testid="breakdown-body" className="mt-3 border border-neutral-200 bg-neutral-50">
      {rows.map(([k, v], i) => (
        <div key={i} className={`flex items-center justify-between gap-3 px-3 py-2 ${i !== rows.length - 1 ? "border-b border-neutral-200" : ""}`}>
          <span className="mono text-[10px] text-neutral-600">{k}</span>
          <span className="mono text-xs font-semibold">{v}</span>
        </div>
      ))}
      <p className="mono text-[9px] text-neutral-500 px-3 py-2 border-t border-neutral-200 leading-relaxed">Your position pays out every business day. "Received so far" is what has been paid to you to date; "Remaining owed" is the rest of your fixed total return still to come. You can withdraw once your principal has been repaid.</p>
    </div>
  );
}

function BankForm({ bank, authHeaders, onSaved }) {
  const [f, setF] = useState({ bank_name: "", account_name: "", account_number: "", routing_number: "", swift: "", bank_address: "", notes: "", ...(bank || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { await api.put("/portal/me/bank", f, authHeaders); toast.success("Wire details saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const F = [["bank_name", "Bank Name"], ["account_name", "Account Name"], ["account_number", "Account Number"], ["routing_number", "Routing Number"], ["swift", "SWIFT / BIC (optional)"], ["bank_address", "Bank Address"]];
  return (
    <div className="p-4 border-t border-black" data-testid="bank-form">
      <p className="mono text-[10px] text-neutral-500 mb-4 leading-relaxed">Saved securely for Canihavemoney LLC to send your payouts. Only the admin can view this.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {F.map(([k, lbl]) => (
          <div key={k}>
            <label className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{lbl}</label>
            <input data-testid={`bank-${k}`} value={f[k] || ""} onChange={set(k)} className={`${field} mt-1.5 !h-11`} />
          </div>
        ))}
      </div>
      <button data-testid="bank-save" disabled={busy} onClick={save} className="w-full mt-4 h-12 bg-black text-white mono text-xs uppercase tracking-[0.2em] hover:bg-neutral-800 disabled:opacity-60">{busy ? "Saving…" : "Save Wire Details"}</button>
    </div>
  );
}

function PayoutModal({ available, hasBank, authHeaders, onClose, onOpenBank, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amt = Number(String(amount).replace(/[^0-9.]/g, ""));
    if (!amt || amt <= 0) return toast.error("Enter a payout amount");
    setBusy(true);
    try { await api.post("/portal/payout", { amount: amt, note }, authHeaders); toast.success("Payout request sent"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Request a Payout" onClose={onClose} testid="payout-modal">
      {!hasBank ? (
        <div>
          <p className="text-sm text-neutral-700 mb-4">Please add your wire/bank details first so we know where to send your funds.</p>
          <button data-testid="payout-add-bank" onClick={onOpenBank} className="w-full h-12 bg-black text-white mono text-xs uppercase tracking-[0.2em]">Add Wire Details</button>
        </div>
      ) : (available || 0) <= 0 ? (
        <div data-testid="payout-locked">
          <p className="text-sm text-neutral-700 mb-2">No funds are available to withdraw yet.</p>
          <p className="mono text-[11px] text-neutral-500 leading-relaxed">You can withdraw once your principal has been fully repaid. After that, your principal becomes available, and your profit unlocks at the end of the term.</p>
        </div>
      ) : (
        <>
          <p className="mono text-[10px] text-neutral-500 mb-4">Available to withdraw: {fmt2(available)}</p>
          <label className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Amount to request</label>
          <div className="relative mt-1.5 mb-4">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 mono text-sm text-neutral-500">$</span>
            <input data-testid="payout-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className={`${field} !h-12 pl-8`} placeholder="e.g. 5000" />
          </div>
          <button type="button" data-testid="payout-max" onClick={() => setAmount(String(Math.round((available || 0) * 100) / 100))} className="block mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-black mb-4">Use full available</button>
          <label className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Note (optional)</label>
          <textarea data-testid="payout-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full mt-1.5 rounded-none border border-black bg-white p-3 text-sm focus:outline-none" placeholder="Anything we should know?" />
          <button data-testid="payout-submit" disabled={busy} onClick={submit} className="w-full mt-4 h-12 bg-black text-white mono text-xs uppercase tracking-[0.2em] hover:bg-neutral-800 disabled:opacity-60">{busy ? "Sending…" : "Send Payout Request"}</button>
        </>
      )}
    </Modal>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md bg-white border border-black max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-black px-5 py-3 sticky top-0 bg-white">
          <span className="mono text-xs uppercase tracking-[0.2em]">{title}</span>
          <button data-testid={`${testid}-close`} onClick={onClose} className="text-neutral-500 hover:text-black text-lg leading-none">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const WCell = ({ label, v, green }) => (
  <div>
    <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">{label}</div>
    <div className={`text-base sm:text-lg font-bold mt-1 ${green ? "text-green-400" : ""}`}>{v}</div>
  </div>
);

const Cell = ({ label, v, big, green }) => (
  <div>
    <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
    <div className={`${big ? "text-lg font-black tracking-tighter" : "text-base font-bold"} ${green ? "text-green-700" : ""}`}>{v}</div>
  </div>
);
